import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Globe, Settings, Link2 } from 'lucide-react';
import { useAuth } from '@/auth/auth0-provider';
import { useAppCache } from '@/contexts/AppCacheContext';
import { useToast } from '@/contexts/ToastContext';
import { useConfirm } from '@/contexts/ConfirmContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { domainsApi, Domain } from '@/api/domainsApi';
import { useAttachDomain } from '@/hooks/useAttachDomain';
import { useDetachDomain } from '@/hooks/useDetachDomain';
import {
  getDomainStatus,
  DomainStatusBadge,
  DomainTypeBadge,
  DomainCell,
  DetachDomainButton,
} from '@/components/domains';

interface SiteDomainManagerProps {
  siteId: string;
  subdomain: string;
  // Site is "paid" iff it has an active Stripe subscription. Custom-domain
  // gating derives from this single flag; there is no separate enabled bit.
  isPaid: boolean;
  onUpgrade: () => void;
  // Domains are owned by SiteDetail so the status card and this component
  // share a single fetch. Both arrays are pre-filtered to type === 'custom'.
  attachedCustomDomains: Domain[];
  domainsLoading: boolean;
  domainsError: string | null;
  // Modal-open state is lifted so the status card can also open the modal.
  attachDomainModalOpen: boolean;
  onAttachDomainModalChange: (open: boolean) => void;
  // Optional section heading rendered inline with the action buttons.
  // When omitted, only the buttons render (right-aligned, prior layout).
  headerTitle?: string;
}

interface AttachDomainModalProps {
  open: boolean;
  onClose: () => void;
  /** Resolves true when the attach succeeded — the modal only closes then. */
  onAttach: (domainId: string) => Promise<boolean>;
  loading: boolean;
}

/**
 * Attach Domain Modal - for attaching existing verified domains to a site
 */
const AttachDomainModal: React.FC<AttachDomainModalProps> = ({
  open,
  onClose,
  onAttach,
  loading
}) => {
  const [selectedDomainId, setSelectedDomainId] = useState<string>('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const { isAuthenticated, isSdkReady } = useAuth();

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [search]);

  const {
    data: domainsData,
    isLoading: loadingDomains,
    isError: domainsLoadFailed,
  } = useQuery({
    queryKey: ['domains', 'available', { search: debouncedSearch }],
    queryFn: () => domainsApi.getAvailableDomains({ search: debouncedSearch, perPage: 50 }),
    enabled: open && isAuthenticated && isSdkReady,
  });
  const domains = domainsData?.items ?? [];

  const handleAttach = async () => {
    if (!selectedDomainId) {
      setValidationError('Please select a domain first');
      return;
    }
    setValidationError(null);
    const attached = await onAttach(selectedDomainId);
    if (!attached) return;
    setSelectedDomainId('');
    setSearch('');
    onClose();
  };

  const handleClose = () => {
    setSelectedDomainId('');
    setSearch('');
    setValidationError(null);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Connect domain to site</DialogTitle>
          <DialogDescription>
            Select a verified domain to connect to this site.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="search-domains">Search Domains</Label>
            <Input
              id="search-domains"
              placeholder="Search by domain name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {loadingDomains ? (
            <div className="p-8 text-center text-muted-foreground">
              Loading domains...
            </div>
          ) : domainsLoadFailed ? (
            <Alert variant="destructive">
              <AlertDescription>
                Failed to load available domains. Please try again.
              </AlertDescription>
            </Alert>
          ) : domains.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground border rounded-2xl">
              <Globe className="h-10 w-10 mx-auto mb-3 opacity-50" />
              <p className="font-medium">No verified domains available</p>
              <p className="text-sm mt-1">Add a domain and verify ownership first.</p>
            </div>
          ) : (
            <div className="max-h-64 overflow-y-auto">
              <Table role="radiogroup" aria-label="Verified domains">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-px pl-6" aria-hidden="true" />
                    <TableHead>Domain</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {domains.map((domain) => {
                    const checked = selectedDomainId === domain.id;
                    return (
                      <TableRow
                        key={domain.id}
                        role="radio"
                        aria-checked={checked}
                        tabIndex={0}
                        onClick={() => {
                          setSelectedDomainId(domain.id);
                          setValidationError(null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setSelectedDomainId(domain.id);
                            setValidationError(null);
                          }
                        }}
                        className={`cursor-pointer transition-colors ${
                          checked ? 'bg-foreground/10' : 'hover:bg-foreground/5'
                        }`}
                      >
                        <TableCell className="w-px pl-6">
                          <span
                            aria-hidden="true"
                            className={`flex h-4 w-4 items-center justify-center rounded-full border ${
                              checked ? 'border-foreground' : 'border-foreground/40'
                            }`}
                          >
                            {checked && <span className="h-2 w-2 rounded-full bg-foreground" />}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="font-medium truncate">{domain.domain}</span>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}

          {validationError && (
            <p className="text-sm text-destructive" role="alert">
              {validationError}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button size="dense" variant="tertiary" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            size="dense"
            onClick={handleAttach}
            disabled={loading}
          >
            {loading ? 'Connecting...' : 'Connect domain'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Main SiteDomainManager component
 */
export const SiteDomainManager: React.FC<SiteDomainManagerProps> = ({
  siteId,
  subdomain,
  isPaid,
  onUpgrade,
  attachedCustomDomains,
  domainsLoading,
  domainsError,
  attachDomainModalOpen,
  onAttachDomainModalChange,
  headerTitle,
}) => {
  const { hasPermission } = useAppCache();
  const toast = useToast();
  const confirm = useConfirm();
  const attachDomain = useAttachDomain();
  const detachDomain = useDetachDomain();

  const canCreate = hasPermission('create:domains');
  const canUpdate = hasPermission('update:domains');

  // Local convenience aliases — the previous fetch produced these directly.
  const domains = attachedCustomDomains;
  const loading = domainsLoading;
  const error = domainsError;

  const handleAttachDomain = async (domainId: string): Promise<boolean> => {
    try {
      await attachDomain.mutateAsync({ domainId, siteId });
      toast.success('Domain connected to this site.', 'Connected');
      return true;
    } catch (err) {
      // Don't surface upstream provider errors (e.g. "Approximated API error:
      // 401") to end users — they're noise for the user and look broken. Log
      // the real error for debugging and show a generic message.
      console.error('Failed to attach domain:', err);
      toast.error('Something went wrong. Please try again.', 'Connect failed');
      return false;
    }
  };

  const handleDetachDomain = async (domainId: string) => {
    const confirmed = await confirm({
      title: 'Disconnect domain',
      description: 'Disconnect this domain from the site? The domain will remain in your account.',
      confirmText: 'Disconnect',
      cancelText: 'Cancel',
      variant: 'destructive',
    });
    if (!confirmed) return;
    try {
      await detachDomain.mutateAsync({ domainId, siteId });
      toast.success('Domain disconnected from this site.', 'Disconnected');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to disconnect domain', 'Disconnect failed');
    }
  };

  const hasActions = canCreate || canUpdate;

  return (
    <div className="space-y-4">
      {(hasActions || headerTitle) && (
        <div className={`flex items-center gap-2 ${headerTitle ? 'justify-between' : 'justify-end'}`}>
          {headerTitle && (
            <h2 className="text-xl font-medium text-foreground">{headerTitle}</h2>
          )}
          {hasActions && (
            <div className="flex items-center gap-2">
          {canCreate && (
            <Button size="dense" variant="tertiary" asChild>
              <Link to="/domains">
                <Settings className="h-4 w-4 mr-1" />
                Manage domains
              </Link>
            </Button>
          )}
          {canUpdate && isPaid && (
            <Button
              variant="primary"
              size="dense"
              onClick={() => onAttachDomainModalChange(true)}
            >
              <Link2 className="h-4 w-4 mr-1" />
              Connect domain
            </Button>
          )}
          {canUpdate && !isPaid && (
            <Button
              variant="primary"
              size="dense"
              onClick={onUpgrade}
            >
              <Link2 className="h-4 w-4 mr-1" />
              Upgrade to connect domain
            </Button>
          )}
            </div>
          )}
        </div>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Domains Table */}
      <div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Domain</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-[140px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {/* Platform Subdomain */}
            <TableRow>
              <TableCell>
                <DomainCell domain={subdomain} type="subdomain" />
              </TableCell>
              <TableCell>
                <DomainTypeBadge type="subdomain" />
              </TableCell>
              <TableCell>
                <DomainStatusBadge status="active" />
              </TableCell>
              <TableCell>
                <span className="text-sm text-muted-foreground">—</span>
              </TableCell>
            </TableRow>

            {/* Loading skeleton — one row per expected custom domain.
                Renders alongside the platform-subdomain row above so the
                table doesn't reflow when real data lands. */}
            {loading && Array.from({ length: 2 }).map((_, i) => (
              <TableRow key={`skel-${i}`}>
                <TableCell><Skeleton className="h-4 w-48" /></TableCell>
                <TableCell><Skeleton className="h-5 w-16" /></TableCell>
                <TableCell><Skeleton className="h-5 w-20" /></TableCell>
                <TableCell><Skeleton className="h-8 w-24" /></TableCell>
              </TableRow>
            ))}

            {/* Custom Domains */}
            {!loading && domains.map((domain) => (
              <TableRow key={domain.id}>
                <TableCell>
                  <DomainCell domain={domain.domain} type="custom" />
                </TableCell>
                <TableCell>
                  <DomainTypeBadge type="custom" />
                </TableCell>
                <TableCell>
                  <DomainStatusBadge status={getDomainStatus(domain)} />
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    {canUpdate && (
                      <DetachDomainButton onClick={() => handleDetachDomain(domain.id)} />
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <AttachDomainModal
        open={attachDomainModalOpen}
        onClose={() => onAttachDomainModalChange(false)}
        onAttach={handleAttachDomain}
        loading={attachDomain.isPending}
      />
    </div>
  );
};

export default SiteDomainManager;
